import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { useLazyQuery, useEvent, useAuth } from '@soap/modules';
import { useUserScopedStorage } from './useUserScopedStorage';
interface Student {
  id: string;
  name: string;
  firstName?: string | null;
  passFailThreshold?: number | null;
  email?: string | null;
  hasOwnAccount?: boolean | null;
  educationSystemKey?: string | null;
}

interface SelectedStudentContextValue {
  selectedStudentId: string | null;
  selectedStudentName: string;
  /** First name for the selected student (from profile, or first word of display name). */
  selectedStudentFirstName: string;
  /** Trimmed value from the student profile. Empty when not set (legacy). */
  selectedStudentEducationSystemKey: string;
  students: Student[];
  isLoading: boolean;
  setSelectedStudent: (studentId: string) => void;
  selectedStudentPassFailThreshold: number; // Default is 50%
}

const SelectedStudentContext = createContext<SelectedStudentContextValue | undefined>(undefined);

// Query parameters constant to avoid repetition
const STUDENTS_QUERY_PARAMS = {
  C158_PageSize: 1000,
  C158_PageNumber: 1,
  C158_IncludeArchived: false
};

/**
 * Provider component that manages selected student state globally.
 * Wrap your app or relevant component tree with this provider.
 * 
 * This provider automatically handles authentication and only fetches students
 * when the user is logged in, preventing unnecessary API calls before login.
 */
export function SelectedStudentProvider({ children }: { children: ReactNode }) {
  const storage = useUserScopedStorage();
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(() => {
    return storage.getSetting('selectedStudentId', null);
  });
  const [students, setStudents] = useState<Student[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Get auth state to ensure we only fetch when authenticated
  const { authReady, isAuthenticated } = useAuth("SelectedStudentProvider");
  
  // Derive selected student name and threshold from students list
  const selectedStudent = students.find(s => s.id === selectedStudentId);
  const selectedStudentName = selectedStudent?.name || '';
  const trimmedFirst = selectedStudent?.firstName?.trim();
  const firstWordOfName = selectedStudentName.trim().split(/\s+/)[0] || '';
  const selectedStudentFirstName = trimmedFirst || firstWordOfName;
  // Default threshold is 50% if not set
  const selectedStudentPassFailThreshold = selectedStudent?.passFailThreshold ?? 50;
  const selectedStudentEducationSystemKey = useMemo(
    () => (selectedStudent?.educationSystemKey ?? '').trim(),
    [selectedStudent]
  );
  
  // Fetch all students - no second parameter to prevent auto-fetch on mount
  const [getStudents] = useLazyQuery("C158v1_GetStudents");

  // Only fetch students when user is authenticated
  useEffect(() => {
    if (authReady === true && isAuthenticated === true) {
      getStudents(STUDENTS_QUERY_PARAMS);
    } else if (authReady === true && isAuthenticated === false) {
      // User is not authenticated, clear loading state
      setIsLoading(false);
      setStudents([]);
      setSelectedStudentId(null);
    }
    // Note: getStudents is intentionally excluded from dependencies as it should be stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authReady, isAuthenticated]);

  // Listen for the students event
  useEvent({
    eventName: "E131v1_GotStudents",
    onEventReceived: (event) => {
      if (event && event.e131_Students) {
        const studentList = event.e131_Students
          .filter((student: any) => !student.e131_Archived)
          .map((student: any) => ({
            id: student.e131_StudentId,
            name: student.e131_Name,
            firstName: student.e131_FirstName,
            passFailThreshold: student.e131_PassFailThreshold,
            email: student.e131_Email,
            hasOwnAccount: student.e131_HasOwnAccount,
            educationSystemKey: student.e131_EducationSystemKey ?? null
          }));
        
        setStudents(studentList);
        validateAndSetSelectedStudent(studentList);
        setIsLoading(false);
      }
    }
  });

  // Helper function to validate and set selected student
  const validateAndSetSelectedStudent = (studentList: Student[]) => {
    if (studentList.length === 0) {
      // No students available - clear selection
      setSelectedStudentId(null);
      storage.setSetting('selectedStudentId', null);
      return;
    }
    
    const savedStudentId = storage.getSetting('selectedStudentId', null);
    
    if (!savedStudentId) {
      // No saved student - select first one
      const firstStudent = studentList[0];
      setSelectedStudentId(firstStudent.id);
      storage.setSetting('selectedStudentId', firstStudent.id);
      return;
    }
    
    // Check if saved student exists in the list
    const studentExists = studentList.some(s => s.id === savedStudentId);
    
    if (!studentExists) {
      // Saved student doesn't exist - select first one
      const firstStudent = studentList[0];
      setSelectedStudentId(firstStudent.id);
      storage.setSetting('selectedStudentId', firstStudent.id);
      return;
    }
    
    // Saved student is valid - use it
    setSelectedStudentId(savedStudentId);
  };

  // Refresh students when they are added, updated, archived, or deleted
  // Only refetch if authenticated
  useEvent({
    eventName: "E133v1_StudentAdded",
    onEventReceived: () => {
      if (isAuthenticated === true) {
        getStudents(STUDENTS_QUERY_PARAMS);
      }
    }
  });

  useEvent({
    eventName: "E135v1_StudentUpdated",
    onEventReceived: () => {
      if (isAuthenticated === true) {
        getStudents(STUDENTS_QUERY_PARAMS);
      }
    }
  });

  useEvent({
    eventName: "E136v1_StudentArchived",
    onEventReceived: () => {
      if (isAuthenticated === true) {
        getStudents(STUDENTS_QUERY_PARAMS);
      }
    }
  });

  useEvent({
    eventName: "E183v1_StudentDeleted",
    onEventReceived: () => {
      if (isAuthenticated === true) {
        getStudents(STUDENTS_QUERY_PARAMS);
      }
    }
  });

  // Function to set selected student (called by dropdown or other components)
  const setSelectedStudent = (studentId: string) => {
    setSelectedStudentId(studentId);
    storage.setSetting('selectedStudentId', studentId);
  };

  const value: SelectedStudentContextValue = {
    selectedStudentId,
    selectedStudentName,
    selectedStudentFirstName,
    selectedStudentEducationSystemKey,
    students,
    isLoading,
    setSelectedStudent,
    selectedStudentPassFailThreshold
  };

  return (
    <SelectedStudentContext.Provider value={value}>
      {children}
    </SelectedStudentContext.Provider>
  );
}

/**
 * Hook to access selected student state from any component.
 * Must be used within a SelectedStudentProvider.
 * 
 * This hook automatically handles authentication and only fetches students
 * when the user is logged in. It prevents queries from running before
 * authentication is complete.
 * 
 * Usage:
 * ```
 * const { selectedStudentId, selectedStudentName, students, isLoading, setSelectedStudent } = useSelectedStudent();
 * 
 * // Wait for loading to complete before firing queries that need a student ID
 * useEffect(() => {
 *   if (!isLoading && selectedStudentId) {
 *     // Safe to fire queries here - user is authenticated
 *   }
 * }, [isLoading, selectedStudentId]);
 * ```
 */
export function useSelectedStudent(): SelectedStudentContextValue {
  const context = useContext(SelectedStudentContext);
  if (context === undefined) {
    throw new Error('useSelectedStudent must be used within a SelectedStudentProvider');
  }
  return context;
}
